import type { Temporal } from '@js-temporal/polyfill'
import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { CampaignFrameSequenceRef } from '@lucimark/shared/helpers/buildFrameSequenceRefs'
import type { PlaybackSegment, ResolvedPlaybackSegment, ResolvePlaybackSegmentsOptions } from '@lucimark/shared/helpers/playbackScheduling'
import type {
  IPlaybackScheduleRoot,
  IScheduleRootAssignment
} from '@lucimark/shared/models/player-registration-payload'
import type { TFrame } from '@lucimark/shared/models/playlist'
import type { IQueueDurationSpineAudit } from '@/helpers/playerDiagnostics/queueDurationSpine'
import type { IQueueFrame } from '@/types'
import { registrationScheduleFrameOverlaysFingerprint } from '@lucimark/shared/helpers/embeddedPlaylistFramesSelectionFingerprint'
import { normalizePortalHouseEditorialDurations } from '@lucimark/shared/helpers/normalizePortalHouseEditorialDurations'
import {
  getPlaybackScheduleMaterializationIdentity,
  isScheduleRootAssignment
} from '@lucimark/shared/helpers/playbackScheduleMaterializationIdentity'
import {
  playbackSegmentsToWorkerPayload,
  resolvePlaybackSegments,
  scheduleFingerprint
} from '@lucimark/shared/helpers/playbackScheduling'
import { ensureRssDirectoryFrameDuration } from '@lucimark/shared/helpers/rssDirectoryFrame'
import {
  resetEmbeddedFrameSelectionState,
  restoreEmbeddedSelectionState,
  snapshotEmbeddedSelectionState
} from '@lucimark/shared/helpers/selectEmbeddedFrames'
import {
  stripPortalBookBlackTailFillerFrames,
  stripSandwichedPortalBlackTailFillerFrames
} from '@lucimark/shared/helpers/stripSandwichedPortalBlackTailFillerFrames'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { buildQueueDurationSpineRows } from '@/helpers/playerDiagnostics/queueDurationSpine'
import { getPlaybackWorkerPayloadSequenceHash, HASH_EMPTY } from '@/helpers/queueHash'
import { canonicalStringify } from '@/workers/registrationStatusWorkerHash'

function allowedAspectRatioSetsEqual(
  a: ReadonlySet<AspectRatio> | null,
  b: ReadonlySet<AspectRatio> | null
): boolean {
  if (a === b) {
    return true
  }
  if (a == null || b == null) {
    return a === b
  }
  if (a.size !== b.size) {
    return false
  }
  for (const x of a) {
    if (!b.has(x)) {
      return false
    }
  }
  return true
}

/**
 * Read-only playlist materialization for playback.
 * Resolves {@link resolvePlaybackSegments} against the effective {@link IPlaybackScheduleRoot}
 * from registration ({@link IScheduleRootAssignment} extends this root).
 */
export default class PlaylistSnapshotService {
  private _customerRoot: IPlaybackScheduleRoot | null = null
  /**
   * Segments from the last {@link resolveQueueFramesAt} run. {@link selectEmbeddedFrames} mutates
   * module state per resolve; {@link getWorkerPlaybackPlanNow} must not call
   * {@link resolvePlaybackSegments} again or the worker plan desyncs from the queue
   * (embedded rotating / random).
   */
  private _lastQueueSegments: ResolvedPlaybackSegment[] | null = null
  /** Queue rows from the last {@link resolveQueueFramesAt} when {@link _lastScheduleFingerprint} still matches. */
  private _lastMaterializedQueue: IQueueFrame[] | null = null
  /** Pre/post {@link normalizePortalHouseEditorialDurations} spine from the last materialize pass. */
  private _lastDurationSpineAudit: IQueueDurationSpineAudit | null = null
  /**
   * Fingerprint of parent-level schedule + embed **slots** at last materialize (see {@link scheduleFingerprint}).
   * When unchanged, {@link peekWorkerSequenceHashAt} returns the hash of {@link _lastQueueSegments} so the interval
   * tick does not think the playable sequence changed just because a full resolve would advance rotating picks.
   */
  private _lastScheduleFingerprint: string | null = null
  /** When true, the next {@link resolveQueueFramesAt} re-runs {@link resolvePlaybackSegments} (loop wrap / forced rebuild). */
  private _forceNextQueueResolve = false
  /**
   * Memoizes `selectionMode: random` embedded draws for the current configured assignment so periodic
   * `rebuildQueue()` does not re-draw every second (would churn queue hash and reset the playback worker).
   */
  private readonly _embeddedRandomPickCache = new Map<string, TFrame[]>()
  /**
   * Memoizes schedule-root random playback ref order per schedule fingerprint key
   * so periodic queue rebuilds do not reshuffle every tick.
   */
  private readonly _scheduleRootRandomRefOrderCache = new Map<string, CampaignFrameSequenceRef[]>()
  /** When non-null and non-empty, {@link resolvePlaybackSegments} only includes matching frame layouts. */
  private _allowedAspectRatios: ReadonlySet<AspectRatio> | null = null
  /** When true, omit frames with any video facet (low-capacity video skip). */
  private _excludeFramesWithVideo = false
  /** Viewport aspect for Lucimark Book paid orders (always applied when registered). */
  private _portalBookViewportAspect: AspectRatio | null = null
  private _operatorMasterPlaylistUrn: string | null = null
  /** Resolved GPS → venue fallback for assignment geofilters. */
  private _geoPoint: import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null = null
  /** True only during {@link PlaybackScheduleLoader.rebuildQueueClearingEmbeddedRandomCache} (loop wrap). */
  private _commitPortalBookHouseRotatingAdvance = false

  private _resolveSegmentOptions(): ResolvePlaybackSegmentsOptions {
    return {
      embeddedRandomPickCache: this._embeddedRandomPickCache,
      scheduleRootRandomRefOrderCache: this._scheduleRootRandomRefOrderCache,
      allowedAspectRatios: this._allowedAspectRatios,
      excludeFramesWithVideo: this._excludeFramesWithVideo || undefined,
      portalBookViewportAspect: this._portalBookViewportAspect,
      operatorMasterPlaylistUrn: this._operatorMasterPlaylistUrn,
      geoPoint: this._geoPoint,
      commitPortalBookHouseRotatingAdvance: this._commitPortalBookHouseRotatingAdvance,
      commitRotatingEmbedAdvance: this._commitPortalBookHouseRotatingAdvance
    }
  }

  /** Same options live materialization uses (aspect / capacity / master bypass). */
  getResolveSegmentOptions(): ResolvePlaybackSegmentsOptions {
    return this._resolveSegmentOptions()
  }

  /** Gate {@link commitPortalBookHouseRotatingAdvances} to loop-wrap rebuilds only. */
  setCommitPortalBookHouseRotatingAdvance(advance: boolean): void {
    this._commitPortalBookHouseRotatingAdvance = advance
  }

  /** Forces the next queue materialize to advance embedded rotating/random picks (full loop wrap). */
  setForceNextQueueResolve(force: boolean): void {
    this._forceNextQueueResolve = force
  }

  /** Clears memoized random embedded picks (e.g. after a full playback loop). */
  clearEmbeddedRandomPickCache(): void {
    this._embeddedRandomPickCache.clear()
  }

  /** Clears embedded random picks and schedule-root random order (registration / assignment swap). */
  clearAllPlaybackPickCaches(): void {
    this._embeddedRandomPickCache.clear()
    this._scheduleRootRandomRefOrderCache.clear()
  }

  getLastQueueDurationSpineAudit(): IQueueDurationSpineAudit | null {
    return this._lastDurationSpineAudit
  }

  configure(params: {
    assignment: IPlaybackScheduleRoot | null
    allowedAspectRatios?: ReadonlySet<AspectRatio> | null
    excludeFramesWithVideo?: boolean
    portalBookViewportAspect?: AspectRatio | null
    operatorMasterPlaylistUrn?: string | null
    geoPoint?: import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null
  }): void {
    const prevRoot = this._customerRoot
    const nextRoot = params.assignment
    /**
     * Same logical assignment + **full** customer root JSON (incl. `embeddedPlaylistFrames`).
     * We intentionally do not use {@link scheduleFingerprint} alone: it tracks embed *slots*, not expanded child
     * payloads, so booked ad updates could keep the same fingerprint while `embeddedPlaylistFrames` changes.
     */
    const prevId = prevRoot != null ? getPlaybackScheduleMaterializationIdentity(prevRoot) : null
    const nextId = nextRoot != null ? getPlaybackScheduleMaterializationIdentity(nextRoot) : null
    const preserveEmbeddedPickCache
      = prevId != null
        && nextId != null
        && prevId === nextId
        && prevRoot !== null
        && nextRoot !== null
        && canonicalStringify(prevRoot) === canonicalStringify(nextRoot)

    const prevAllowed = this._allowedAspectRatios
    let allowedAspectRatiosChanged = false
    if (params.allowedAspectRatios !== undefined) {
      allowedAspectRatiosChanged = !allowedAspectRatioSetsEqual(prevAllowed, params.allowedAspectRatios)
      this._allowedAspectRatios = params.allowedAspectRatios
    }
    let excludeFramesWithVideoChanged = false
    if (params.excludeFramesWithVideo !== undefined) {
      excludeFramesWithVideoChanged = this._excludeFramesWithVideo !== params.excludeFramesWithVideo
      this._excludeFramesWithVideo = params.excludeFramesWithVideo
    }
    if (params.portalBookViewportAspect !== undefined) {
      this._portalBookViewportAspect = params.portalBookViewportAspect
    }
    if (params.operatorMasterPlaylistUrn !== undefined) {
      this._operatorMasterPlaylistUrn = params.operatorMasterPlaylistUrn
    }
    let geoPointChanged = false
    if (params.geoPoint !== undefined) {
      const prev = this._geoPoint
      const next = params.geoPoint
      geoPointChanged = prev?.latitude !== next?.latitude || prev?.longitude !== next?.longitude
      this._geoPoint = next
    }
    const spineInstant = getLocalAsUtcInstant()
    const spineUnchanged = prevRoot != null
      && nextRoot != null
      && scheduleFingerprint(prevRoot, spineInstant, this._resolveSegmentOptions())
      === scheduleFingerprint(nextRoot, spineInstant, this._resolveSegmentOptions())
    const overlayFingerprintUnchanged = registrationScheduleFrameOverlaysFingerprint(prevRoot)
      === registrationScheduleFrameOverlaysFingerprint(nextRoot)
    const sameRootReference = prevRoot != null && nextRoot != null && prevRoot === nextRoot
    const materializationIdentityUnchanged = prevId != null && nextId != null && prevId === nextId
    this._customerRoot = params.assignment
    /**
     * Embed pick decks (rotating/random) may stay when only the schedule spine matches —
     * {@link scheduleFingerprint} is facet-blind by design.
     */
    const preserveEmbedSelection = preserveEmbeddedPickCache
      || spineUnchanged
      || sameRootReference
      || (materializationIdentityUnchanged && overlayFingerprintUnchanged)
    /**
     * Materialized queue rows embed `facet.asset` URNs. Spine-only equality must **not** preserve
     * that cache: an on-air asset swap keeps the same spine/identity while
     * {@link registrationScheduleFrameOverlaysFingerprint} changes, and assets refresh from the live
     * root — leaving stale facet URNs on the queue while the asset index drops the old binaries (#1352).
     */
    const preserveMaterializedQueue
      = (preserveEmbeddedPickCache
        || sameRootReference
        || (materializationIdentityUnchanged && overlayFingerprintUnchanged && spineUnchanged))
      && !allowedAspectRatiosChanged
      && !excludeFramesWithVideoChanged
      && !geoPointChanged
    if (!preserveMaterializedQueue) {
      this._lastQueueSegments = null
      this._lastScheduleFingerprint = null
      this._lastMaterializedQueue = null
    }
    if (!preserveEmbedSelection) {
      this.clearAllPlaybackPickCaches()
      /** Clears module-level random/rotating decks in {@link selectEmbeddedFrames} (same schedule spine can change embed payloads). */
      resetEmbeddedFrameSelectionState()
    }
  }

  /**
   * {@link scheduleFingerprint} for any root using this snapshot’s resolver options (aspect + embed caches).
   * Lets callers compare two assignments before {@link configure} to ignore benign registration JSON churn.
   */
  scheduleSpineFingerprintForRoot(root: IPlaybackScheduleRoot | null, referenceInstant: Temporal.Instant): string {
    return scheduleFingerprint(root, referenceInstant, this._resolveSegmentOptions())
  }

  /** Effective schedule from registration. */
  getEffectivePlaybackSchedule(): IPlaybackScheduleRoot | null {
    return this._customerRoot
  }

  /**
   * When registration still sends a legacy campaign assignment, returns it.
   * Pure {@link IPlaybackScheduleRoot} without `scheduleRootUrn` returns null — UI should use {@link getEffectivePlaybackSchedule}.
   */
  getEffectiveAssignment(): IScheduleRootAssignment | null {
    const eff = this.getEffectivePlaybackSchedule()
    return isScheduleRootAssignment(eff) ? eff : null
  }

  /**
   * Worker sequence hash if {@link resolvePlaybackSegments} ran at `referenceInstant`,
   * without mutating {@link _lastQueueSegments} or the materialized queue (for cheap interval checks).
   */
  peekWorkerSequenceHashAt(referenceInstant: Temporal.Instant): string {
    const schedule = this.getEffectivePlaybackSchedule()
    if (schedule === null || (schedule.playlists?.length ?? 0) === 0) {
      return HASH_EMPTY
    }
    const scheduleFp = scheduleFingerprint(schedule, referenceInstant, this._resolveSegmentOptions())
    if (
      this._lastQueueSegments !== null
      && this._lastQueueSegments.length > 0
      && this._lastScheduleFingerprint !== null
      && scheduleFp === this._lastScheduleFingerprint
    ) {
      return getPlaybackWorkerPayloadSequenceHash(
        playbackSegmentsToWorkerPayload(this._lastQueueSegments)
      )
    }
    const snap = snapshotEmbeddedSelectionState()
    try {
      const segments = resolvePlaybackSegments(schedule, referenceInstant, this._resolveSegmentOptions())
      return getPlaybackWorkerPayloadSequenceHash(
        playbackSegmentsToWorkerPayload(segments)
      )
    }
    finally {
      restoreEmbeddedSelectionState(snap)
    }
  }

  /**
   * Slim timing plan for the playback scheduler worker: same order and sequence hash as
   * {@link resolveQueueFramesAt} at the same instant (durations + ids only; no facets).
   */
  resolveWorkerPlaybackPlanAt(_referenceInstant: Temporal.Instant): PlaybackSegment[] {
    const schedule = this.getEffectivePlaybackSchedule()
    if (schedule === null || (schedule.playlists?.length ?? 0) === 0) {
      this._lastQueueSegments = null
      this._lastScheduleFingerprint = null
      return []
    }
    if (this._lastQueueSegments !== null) {
      return playbackSegmentsToWorkerPayload(this._lastQueueSegments)
    }
    const segments = resolvePlaybackSegments(schedule, _referenceInstant, this._resolveSegmentOptions())
    this._lastQueueSegments = segments
    return playbackSegmentsToWorkerPayload(this._lastQueueSegments)
  }

  /**
   * Resolve queue rows at `referenceInstant` without mutating {@link _lastQueueSegments} or embed module state.
   */
  peekQueueFramesAt(referenceInstant: Temporal.Instant): IQueueFrame[] {
    const schedule = this.getEffectivePlaybackSchedule()
    if (schedule === null || (schedule.playlists?.length ?? 0) === 0) {
      return []
    }
    const snap = snapshotEmbeddedSelectionState()
    try {
      const segments = resolvePlaybackSegments(schedule, referenceInstant, this._resolveSegmentOptions())
      return this._materializeQueueFramesFromSegments(schedule, segments).queue
    }
    finally {
      restoreEmbeddedSelectionState(snap)
    }
  }

  resolveQueueFramesAt(referenceInstant: Temporal.Instant): IQueueFrame[] {
    const schedule = this.getEffectivePlaybackSchedule()
    if (schedule === null || (schedule.playlists?.length ?? 0) === 0) {
      this._lastQueueSegments = null
      this._lastScheduleFingerprint = null
      this._lastMaterializedQueue = null
      return []
    }

    const scheduleFp = scheduleFingerprint(schedule, referenceInstant, this._resolveSegmentOptions())
    const forceResolve = this._forceNextQueueResolve
    this._forceNextQueueResolve = false
    if (
      !forceResolve
      && this._lastMaterializedQueue !== null
      && this._lastQueueSegments !== null
      && this._lastQueueSegments.length > 0
      && this._lastScheduleFingerprint !== null
      && scheduleFp === this._lastScheduleFingerprint
    ) {
      return this._lastMaterializedQueue
    }

    const segments = resolvePlaybackSegments(schedule, referenceInstant, this._resolveSegmentOptions())
    this._lastScheduleFingerprint = scheduleFp
    const { queue, adjustedSegments } = this._materializeQueueFramesFromSegments(schedule, segments)
    this._lastQueueSegments = adjustedSegments
    this._lastMaterializedQueue = queue
    return queue
  }

  private _materializeQueueFramesFromSegments(
    schedule: IPlaybackScheduleRoot,
    segments: ResolvedPlaybackSegment[]
  ): { queue: IQueueFrame[], adjustedSegments: ResolvedPlaybackSegment[] } {
    const queue: IQueueFrame[] = []
    for (const { playlistUrn, contentSourcePlaylistUrn, playlistSeqNo, frame, playlistWidgetConfig } of segments) {
      queue.push({
        ...frame,
        playlistUrn,
        contentSourcePlaylistUrn,
        playlistSeqNo,
        campaignWidgetConfig: schedule.widgetConfig ?? null,
        playlistWidgetConfig: playlistWidgetConfig ?? null
      })
    }
    const stripped = stripPortalBookBlackTailFillerFrames(
      stripSandwichedPortalBlackTailFillerFrames(queue)
    )
    const normalized = normalizePortalHouseEditorialDurations(stripped).map(ensureRssDirectoryFrameDuration)
    this._lastDurationSpineAudit = {
      published: buildQueueDurationSpineRows(stripped),
      materialized: buildQueueDurationSpineRows(normalized)
    }
    const segmentByQueueKey = new Map(segments.map(s => [`${s.playlistSeqNo}:${s.frame.id}`, s]))
    const adjustedSegments: ResolvedPlaybackSegment[] = []
    for (const row of normalized) {
      const seg = segmentByQueueKey.get(`${row.playlistSeqNo}:${row.id}`)
      if (seg == null) {
        continue
      }
      const d = row.duration
      adjustedSegments.push(
        d != null && seg.frame.duration !== d
          ? { ...seg, frame: { ...seg.frame, duration: d } }
          : seg
      )
    }
    return { queue: normalized, adjustedSegments }
  }
}
